/**
 * useExpandedSections Hook
 *
 * Manages the expand/collapse state for all dashboard sections
 */

import { useState, useCallback } from 'react';
import { ExpandedSections } from '../types';

const DEFAULT_EXPANDED_SECTIONS: ExpandedSections = {
    orgOkr: true,
    teamContributions: true,
    departments: true,
    execution: true,
    notes: true,
    pastOkrs: false,
    teamMembers: true,
    teamObjectiveManagement: true,
    milestonesExecution: true,
    feedbackGuidance: true
};

export interface UseExpandedSectionsReturn {
    expandedSections: ExpandedSections;
    toggleSection: (section: keyof ExpandedSections) => void;
    expandAll: () => void;
    collapseAll: () => void;
    setExpandedSections: React.Dispatch<React.SetStateAction<ExpandedSections>>;
}

export const useExpandedSections = (
    initialState: Partial<ExpandedSections> = {}
): UseExpandedSectionsReturn => {
    const [expandedSections, setExpandedSections] = useState<ExpandedSections>({
        ...DEFAULT_EXPANDED_SECTIONS,
        ...initialState
    });

    const toggleSection = useCallback((section: keyof ExpandedSections) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    }, []);

    const expandAll = useCallback(() => {
        setExpandedSections({
            orgOkr: true,
            teamContributions: true,
            departments: true,
            execution: true,
            notes: true,
            pastOkrs: true,
            teamMembers: true,
            teamObjectiveManagement: true,
            milestonesExecution: true,
            feedbackGuidance: true
        });
    }, []);

    const collapseAll = useCallback(() => {
        setExpandedSections({
            orgOkr: false,
            teamContributions: false,
            departments: false,
            execution: false,
            notes: false,
            pastOkrs: false,
            teamMembers: false,
            teamObjectiveManagement: false,
            milestonesExecution: false,
            feedbackGuidance: false
        });
    }, []);

    return {
        expandedSections,
        toggleSection,
        expandAll,
        collapseAll,
        setExpandedSections
    };
};

export default useExpandedSections;
